/**
 * 主布局组件 - 侧边栏 + 顶栏 + 内容区
 * 支持响应式 H5 适配
 */

import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';
import { authApi } from '../../api';
import { ToastProvider, toast } from '../common/Toast';
import { CardExpireAlert } from './CardExpireAlert';
import styles from './Layout.module.css';

const NAV_ITEMS = [
  { path: '/send',      label: '消息发送', icon: '📨' },
  { path: '/rules',     label: '规则设置', icon: '⚙️' },
  { path: '/feishu-app',label: '飞书应用', icon: '🔧' },
  { path: '/watermark', label: '水印设置', icon: '🖋️' },
  { path: '/card',      label: '卡密管理', icon: '🔑' },
  { path: '/logs',      label: '操作记录', icon: '📋' },
];

const ADMIN_NAV_ITEMS = [
  { path: '/admin/dashboard', label: '控制台', icon: '🏠' },
  { path: '/admin/users', label: '用户管理', icon: '👥' },
  { path: '/admin/cards', label: '卡密管理', icon: '💳' },
  { path: '/admin/configs', label: '配置管理', icon: '🗂️' },
  { path: '/admin/logs', label: '系统日志', icon: '📊' },
];

function Layout() {
  const { user, is_admin, is_card_expiring_soon, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebar_open, set_sidebar_open] = useState(false);

  // 监听卡密过期事件
  useEffect(() => {
    const handler = (e) => {
      toast.error(e.detail.message || '卡密已过期，请续费');
    };
    window.addEventListener('card:expired', handler);
    return () => window.removeEventListener('card:expired', handler);
  }, []);

  // 路由变化时关闭移动端侧边栏
  useEffect(() => {
    set_sidebar_open(false);
  }, [location.pathname]);

  const handle_logout = async () => {
    try {
      await authApi.logout();
    } catch {}
    logout();
    navigate('/login');
    toast.success('已退出登录');
  };

  // 管理员和管理员需要同时看到两套导航（或合并）
  // 这里我们合并导航，让管理员也能看到会员的菜单
  const nav_items = is_admin ? [...ADMIN_NAV_ITEMS, ...NAV_ITEMS] : NAV_ITEMS;

  return (
    <div className={styles.app}>
      <ToastProvider />

      {/* 卡密快过期提醒 */}
      {is_card_expiring_soon && !is_admin && (
        <CardExpireAlert expire_at={user?.card_expire_at} />
      )}

      {/* 顶部导航栏 */}
      <header className={styles.header}>
        <div className={styles['header-left']}>
          <button
            className={styles['menu-btn']}
            onClick={() => set_sidebar_open(!sidebar_open)}
            aria-label="菜单"
          >
            <span className={styles['menu-icon']}>
              {sidebar_open ? '✕' : '☰'}
            </span>
          </button>
          <div className={styles.logo}>
            <span className={styles['logo-icon']}>🚀</span>
            <span className={styles['logo-text']}>FDPush</span>
          </div>
        </div>

        <div className={styles['header-right']}>
          {is_admin && (
            <span className={styles['admin-badge']}>管理员</span>
          )}
          <span className={styles.username}>{user?.username}</span>
          {is_admin && (
            <button
              className={styles['change-pwd-btn']}
              onClick={() => navigate('/change-password')}
              title="修改密码"
            >
              改密码
            </button>
          )}
          <button className={styles['logout-btn']} onClick={handle_logout}>
            退出
          </button>
        </div>
      </header>

      <div className={styles.container}>
        {/* 遮罩层（移动端） */}
        {sidebar_open && (
          <div
            className={styles.overlay}
            onClick={() => set_sidebar_open(false)}
          />
        )}

        {/* 侧边栏 */}
        <aside className={`${styles.sidebar} ${sidebar_open ? styles['sidebar-open'] : ''}`}>
          <nav className={styles.nav}>
            {nav_items.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `${styles['nav-item']} ${isActive ? styles['nav-item-active'] : ''}`
                }
              >
                <span className={styles['nav-icon']}>{item.icon}</span>
                <span className={styles['nav-label']}>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* 卡密到期信息 */}
          {!is_admin && user?.card_expire_at && (
            <div className={styles['card-info']}>
              <div className={styles['card-info-title']}>卡密到期</div>
              <div className={styles['card-info-date']}>
                {new Date(user.card_expire_at).toLocaleDateString('zh-CN')}
              </div>
            </div>
          )}
        </aside>

        {/* 主内容区 */}
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
